import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { img } from "../../constants/assets";
import BrandsMarquee from "../../components/BrandsMarquee";
import PartnerModal from "../../components/PartnerModal";
import { useBookDemo } from "../../context/BookDemoContext";
import { FeatureIcon } from "../../components/GlobalConnected";
import BlurRevealText from "../../components/BlurRevealText";

// Order matches industries.industriesServed in the locale files.
const industryImages = [
  { image: "hospitality.svg", icon: "hospitality-icon.svg" }, // Airlines
  { image: "transport.svg", icon: "hospitality-icon.svg" }, // Travel Agencies
  { image: "hospitality.svg", icon: "transport-icon.svg" }, // OTAs
  { image: "financial.svg", icon: "financial-icon.svg" }, // Neobanks
  { image: "healthcare.svg", icon: "financial-icon.svg" }, // Insurance
  { image: "Retail.svg", icon: "retail-icon.svg" }, // Retail
  { image: "transport.svg", icon: "transport-icon.svg" }, // Telecom
  { image: "healthcare.svg", icon: "retail-icon.svg" }, // Enterprise
  { image: "transport.svg", icon: "transport-icon.svg" }, // Mobility Apps
  { image: "financial.svg", icon: "financial-icon.svg" }, // Digital Wallets
  { image: "Retail.svg", icon: "hospitality-icon.svg" }, // Membership Platforms
];



const aiCardIcons = ["airecive.svg", "routing.svg", "fraud.svg", "demand.svg"];
const ctaFeatureIcons = ["globe", "tower", "shield", "code"];
function CtaRocketIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.03-2.91l-2.91-2.91c-.78-.73-2.07-.74-2.91-.03z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M9 12H4s.55-3.03 2-5c1.62-2.2 5-3 5-3M15 12h5s-.55-3.03-2-5c-1.62-2.2-5-3-5-3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CtaHeadsetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 11v4a2 2 0 0 0 2 2h1v-8H5a2 2 0 0 0-2 2zM19 11v4a2 2 0 0 1-2 2h-1v-8h1a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M7 15v2a5 5 0 0 0 10 0v-2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M12 3a5 5 0 0 0-5 5v3h10V8a5 5 0 0 0-5-5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function RailArrowIcon({ direction }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={direction === "prev" ? "M15 5 8 12l7 7" : "m9 5 7 7-7 7"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Industries() {
  const { t } = useTranslation();
  const railRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateRailNav = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const maxScroll = rail.scrollWidth - rail.clientWidth;
    setCanScrollPrev(rail.scrollLeft > 4);
    setCanScrollNext(rail.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    updateRailNav();
    rail.addEventListener("scroll", updateRailNav, { passive: true });
    window.addEventListener("resize", updateRailNav);
    return () => {
      rail.removeEventListener("scroll", updateRailNav);
      window.removeEventListener("resize", updateRailNav);
    };
  }, [updateRailNav]);

  const scrollRail = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector(".industry-card");
    const step = card
      ? card.getBoundingClientRect().width + 24
      : rail.clientWidth * 0.8;
    rail.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const howItWorksSteps = t("home.howItWorksSteps", { returnObjects: true });
  const aiCards = t("home.aiCards", { returnObjects: true });
  const platformFeatures = t("home.platformFeatures", { returnObjects: true });
  const ctaFeatures = t("ctaBanner.features", { returnObjects: true });

  const { openBookDemo } = useBookDemo();
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const industriesServed = t("industries.industriesServed", { returnObjects: true });
  const connectivityFeatures = t("industries.connectivityFeatures", {
    returnObjects: true,
  });
  const ctaTitleSegments = [
    { text: t("ctaBanner.titleBefore") },
    {
      text: t("ctaBanner.titleHighlight"),
      className: "cta-banner-accent",
      breakAfter: true,
    },
    { text: t("ctaBanner.titleAfter") },
  ];

  
  return (
    <>
      <section className="industries-hero">
        <div className="container py-2">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-md-6 d-flex hero-content">
              <div className="animate__animated animate__fadeInLeftBig animate__slow">
                <p className="banner-sub-heading p-2">
                  {t("industries.heroOverline")}
                </p>
                <h1 className="solutions-hero-title">
                  <Trans
                    i18nKey="industries.heroTitle"
                    components={{ 1: <span className="gradient-text-span" /> }}
                  />
                </h1>
                <p className="pera-cs solutions-hero-desc">
                  {t("industries.heroDesc")}
                </p>
                <div className="btn-group gap-3">
                  <Link
                    className="btn-cs btn-booking py-3 fw-normal text-decoration-none"
                    to="/solutions"
                  >
                    {t("industries.exploreAll")}{" "}
                    <span className="img-fluid">
                      <img src={img("arrow.svg")} alt="" />
                    </span>
                  </Link>
                  <button
                    className="btn-cs btn-lang d-flex gap-2 py-3 fw-normal"
                    type="button"
                  >
                    {t("common.exploreDemo")}
                    <span className="img-fluid">
                      <img src={img("Border.svg")} alt="" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="working py-50">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <p className="brands-heading">
                {t("common.trustedBy")} <span>{t("common.worldwide")}</span>
              </p>
              <BrandsMarquee />
            </div>
          </div>
        </div>
      </section>

      <section className="platform-section py-50">
        <div className="container">
         
          <div className="industry-rail-wrap mt-2">
             <p className="banner-sub-heading p-2 mx-auto bg-transparent">
                {t("industries.serveOverline")}
              </p>
              <h2 className="heading-cs">{t("industries.serveTitle")}</h2>
            <button
              type="button"
              className="industry-rail-nav industry-rail-nav--prev"
              onClick={() => scrollRail(-1)}
              disabled={!canScrollPrev}
              aria-label={t("industries.scrollPrev")}
            >
              <RailArrowIcon direction="prev" />
            </button>

            <div className="industry-rail" ref={railRef}>
              {industriesServed.map((item, index) => (
                <article className="industry-card" key={item.title}>
                  <div className="industry-card-media">
                    <img src={img(industryImages[index].image)} alt="" decoding="async" />
                    <span className="industry-card-icon">
                      <img
                        src={img(industryImages[index].icon)}
                        alt=""
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                  <div className="industry-card-body">
                    <h3 className="industry-card-title">{item.title}</h3>
                    <p className="industry-card-text">{item.description}</p>
                    <Link
                      className="industry-card-link text-decoration-none"
                      to="/solutions"
                    >
                      {t("industries.exploreSolutions")}
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <button
              type="button"
              className="industry-rail-nav industry-rail-nav--next"
              onClick={() => scrollRail(1)}
              disabled={!canScrollNext}
              aria-label={t("industries.scrollNext")}
            >
              <RailArrowIcon direction="next" />
            </button>
          </div>
        </div>
      </section>

      <section className="succed-sec py-50 smarter-connectivity">
        <div className="container">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-lg-6 hero-content h-auto">
              <h2 className="inner-heading mb-0">{t("industries.smarterTitle1")}</h2>
              <h2 className="inner-heading">
                <span className="gradient-text-span">
                  {t("industries.smarterTitle2")}
                </span>
              </h2>
              <div className="row g-3 why-choose-cards mt-2">
                {connectivityFeatures.map((item) => (
                  <div className="col-md-6" key={item.title}>
                    <article className="why-choose-card d-flex gap-3 align-items-start h-100 p-0">
                      <span className="platform-check" aria-hidden="true">
                        <img src={img("check.svg")} alt="" />
                      </span>
                      <div>
                        <h3 className="why-choose-card-title">{item.title}</h3>
                        <p className="why-choose-card-text">{item.description}</p>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="platform-media">
                <img
                  className="connection-bg"
                  src={img("connection-bg.svg")}
                  alt={t("industries.connectionImageAlt")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-banner-section industry-partners">
        <div className="container">
          <div className="cta-banner">
            <div className="row align-items-center g-4">
              <div className="col-lg-7">
                <p className="cta-banner-label">
                  {t("ctaBanner.label")}
                </p>
                <BlurRevealText
                  as="h2"
                  className="cta-banner-title"
                  segments={ctaTitleSegments}
                />
                <p className="cta-banner-text">
                  <Trans
                    i18nKey="ctaBanner.subtitle"
                    components={{ 1: <span className="cta-banner-accent" /> }}
                  />
                </p>
                <div className="cta-banner-features">
                  {ctaFeatures.map((item, index) => (
                    <div className="cta-banner-feature" key={item.label}>
                      <FeatureIcon type={ctaFeatureIcons[index]} />
                      <div>
                        <strong className="cta-banner-stat">{item.stat}</strong>
                        <span className="cta-banner-feature-label">
                          {item.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-lg-5">
                <div className="cta-banner-actions">
                  <button
                    className="cta-btn-primary"
                    type="button"
                    onClick={() => setIsPartnerModalOpen(true)}
                  >
                    <span className="cta-btn-icon">
                      <CtaRocketIcon />
                    </span>
                    <span className="cta-btn-label">{t("common.startNow")}</span>
                    <span className="cta-btn-icon">
                      <img src={img("arrow.svg")} alt="" />
                    </span>
                  </button>
                  <button
                    className="cta-btn-secondary"
                    type="button"
                    onClick={openBookDemo}
                  >
                    <span className="cta-btn-icon">
                      <CtaHeadsetIcon />
                    </span>
                    <span className="cta-btn-label">{t("common.talkToSales")}</span>
                    <span className="cta-btn-icon">
                      <img src={img("arrow.svg")} alt="" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerModal
        isOpen={isPartnerModalOpen}
        onClose={() => setIsPartnerModalOpen(false)}
      />
    </>
  );
}

export default Industries;
